import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {debounceTime, map, switchMap, take, tap} from "rxjs/operators";
import {LocalState, ManagerUtils, SortingRule} from "../../services/utils/v3/manager-utils";
import {Page, Pageable} from "../../model/rest/base";
import {CasaRestApiV2} from "../../services/rest/casa-rest.service";
import {LocalStateService} from "../../services/local-state.service";
import {EMPTY, forkJoin, of, Subject, Subscription} from "rxjs";
import {ConfirmDialogService} from "../../services/confirm-dialog.service";
import {ToastNotification, ToastNotificationsService} from "../../services/toast-notifications.service";
import {PersonEmailChangeComponent} from 'src/app/components/person-email-change/person-email-change.component';
import {FunctionalAccessService} from 'src/app/services/functional-access.service';
import {
  PersonPasswordChangeComponent
} from 'src/app/components/person-password-change/person-password-change.component';
import {LearningUnitTeacherStatus, V3Teacher} from '../../model/v3/schools';
import {TeacherV3RestService} from '../../services/rest/v3/teacher-rest.service';
import {WorkflowsRestService} from '../../services/rest/v3/workflows-rest.service';
import {VerifiedEmailChangeRequest, VerifiedPasswordChangeRequest} from '../../model/v3/people';
import {TransactionsV3RestService} from '../../services/rest/v3/transactions-rest.service';

class TeachersSortingRule extends SortingRule {
  constructor(public name: string, sorting: string[], public status: string[]) {
    super(sorting);
  }
  static Natural = new TeachersSortingRule("All",null, null);
  static NameAsc = new TeachersSortingRule("All (Ascending)", ["person.surname,ASC"], null);
  static NameDesc = new TeachersSortingRule("All (Descending)", ["person.surname,DESC"], null);
  static values = [TeachersSortingRule.Natural, TeachersSortingRule.NameAsc, TeachersSortingRule.NameDesc];
}


@Component({
    selector: 'app-manager-teachers-list-page',
    templateUrl: './manager-teachers-list-page.component.html',
    styleUrls: ['./manager-teachers-list-page.component.css'],
    standalone: false
})
export class ManagerTeachersListPageComponent implements OnInit, OnDestroy {
  schoolId: number;
  currentState: LocalState<TeachersSortingRule>;
  private teachersPage: Page<V3Teacher>;
  sortingRules = TeachersSortingRule.values;
  private teachersStateUrl = "dashboard/teachers";
  private searchTermUpdates = new Subject<string>();
  private readonly searchTermUpdatesSubscription: Subscription;
  private dataSubscription: Subscription;

  @ViewChild('emailModal', {static: true})
  emailModal: PersonEmailChangeComponent;
  @ViewChild('passwordModal', {static: true})
  passwordModal: PersonPasswordChangeComponent;

  constructor(
    route: ActivatedRoute,
    private casa: CasaRestApiV2,
    private teacherV3Rest: TeacherV3RestService,
    private transactionsV3Rest: TransactionsV3RestService,
    private workflowsRest: WorkflowsRestService,
    private localState: LocalStateService,
    private router: Router,
    private confirmationService: ConfirmDialogService,
    private toastNotificationService: ToastNotificationsService,
    public functionalAccessService: FunctionalAccessService
  ) {

    this.dataSubscription = forkJoin([
      this.loadSchoolId(route),
      this.findLocalState(),
      this.getTermQueryParam(route)
    ]).pipe(
      switchMap( ([_, state, term]) => {
        if (term) {
          state.term = term
        }
        return this.loadTeachersPage(state);
      })
    ).subscribe();

    this.searchTermUpdatesSubscription = this.searchTermUpdates.pipe(
      debounceTime(400)
    ).subscribe( _ => this.doSearch());
  }

  ngOnDestroy() {
    if (this.searchTermUpdatesSubscription) {
      this.searchTermUpdatesSubscription.unsubscribe();
    }
    this.dataSubscription?.unsubscribe();
  }

  ngOnInit() {
  }

  private getTermQueryParam(route: ActivatedRoute) {
    return route.queryParamMap.pipe(
      take(1),
      map( paramMap => paramMap.get('term'))
    );
  }

  private findLocalState() {
    return this.localState.get<LocalState<TeachersSortingRule>>(
      this.teachersStateUrl,
      () => new LocalState("", TeachersSortingRule.NameAsc ,Pageable.of(0, 50, null)));
  }

  private loadSchoolId(route: ActivatedRoute) {
    return route.parent.paramMap.pipe(
      take(1),
      map( params => Number(params.get('schoolId'))),
      tap<number>( schoolId => this.schoolId = schoolId));
  }

  set searchTerm(value: string) {
    if (this.currentState) this.currentState.term = value;
    this.searchTermUpdates.next(value);
  }

  get searchTerm(): string {
    if (this.currentState) return this.currentState.term;
    return '';
  }

  private loadTeachersPage(state: LocalState<TeachersSortingRule>) {
    this.currentState = state;
    return this.localState.set(this.teachersStateUrl, state).pipe(
      switchMap(state => this.teacherV3Rest.findTeachers(this.schoolId, state.term, null, state.page)),
      tap( studentsPage => this.storeTeachersPage(studentsPage))
    )
  }

  private storeTeachersPage(studentsPage: Page<V3Teacher>) {
    this.teachersPage = studentsPage;
  }

  countTotal() {
    if (!this.teachersPage) return 0;
    return this.teachersPage.totalElements;
  }

  doSearch() {
    this.loadTeachersPage(this.currentState.first()).subscribe();
  }

  searchUpdated() {
    this.currentState.applySorting();
  }

  hasPages() {
    return this.hasPrev() || this.hasNext();
  }

  hasPrev() {
    return this.teachersPage && !this.teachersPage.first;
  }

  getPageNumber() {
    return this.teachersPage.number + 1;
  }

  hasNext() {
    return this.teachersPage && !this.teachersPage.last;
  }

  hasTeachers() {
    return this.teachersPage != null;
  }

  getTeachersPage() {
    return this.teachersPage.content;
  }

  prev() {
    this.loadTeachersPage(this.currentState.prev()).subscribe();
  }

  next() {
    this.loadTeachersPage(this.currentState.next()).subscribe();
  }

  open(teacher: V3Teacher) {
    if(this.schoolId === 1)
      this.router.navigate(['school', this.schoolId, 'teacher', teacher.id, 'lessons']).then();
    else
      this.router.navigate(['school', this.schoolId, 'teacher', teacher.id, 'lessons-list']).then();
  }

  getName(teacher: V3Teacher) {
    return ManagerUtils.getName(teacher.person);
  }

  getMail(teacher: V3Teacher) {
    return teacher.person.registrationEmail;
  }

  getStatus(teacher: V3Teacher) {
    return teacher.status;
  }

  getInitials(teacher: V3Teacher) {
    return ManagerUtils.getInitials(teacher.person)
  }

  delete(teacher: V3Teacher) {
    this.confirmationService.doOnConfirm("Are you sure you want delete the teacher?",
      () => {
        this.transactionsV3Rest.isTeacherSubscribed(this.schoolId, teacher.id).pipe(
          switchMap(isSubscribed => {
            if (isSubscribed) {
              this.toastNotificationService.display(new ToastNotification("Teacher deletion", "Teacher has active books subscription. Cancel it before deletion."));
              return EMPTY;
            }
            return this.teacherV3Rest.deleteTeacher(this.schoolId, teacher.id);
          }),
          tap(_ => this.toastNotificationService.display(new ToastNotification("Teacher deletion",`Teacher ${this.getName(teacher)} has been deleted.`))),
          switchMap( _ => this.loadTeachersPage(this.currentState))
        ).subscribe();
      },
      "Teacher deletion",
      "bg-danger text-white"
    );
  }

  block(teacher: V3Teacher) {
    this.confirmationService.doOnConfirm("Are you sure you want to block the teacher?",
      () => {
        this.transactionsV3Rest.isTeacherSubscribed(this.schoolId, teacher.id).pipe(
          switchMap(isSubscribed => {
            if (isSubscribed) {
              this.toastNotificationService.display(new ToastNotification("Teacher blocking", "Teacher has active books subscription. Cancel it before blocking."));
              return EMPTY;
            }
            return this.teacherV3Rest.changeTeacherStatus(this.schoolId, teacher.id, LearningUnitTeacherStatus.BLOCKED);
          }),
          tap(_ => this.toastNotificationService.display(new ToastNotification("Teacher blocking",`Teacher ${this.getName(teacher)} has been blocked.`))),
          switchMap( _ => this.loadTeachersPage(this.currentState))
        ).subscribe();
      },"Teacher blocking", "bg-warning");
  }

  activate(teacher: V3Teacher) {
    this.confirmationService.doOnConfirm("Are you sure you want to activate the teacher?",
      () => {
        this.teacherV3Rest.changeTeacherStatus(this.schoolId, teacher.id, LearningUnitTeacherStatus.ACTIVE).pipe(
          tap(_ => this.toastNotificationService.display(new ToastNotification("Teacher activation",`Teacher ${this.getName(teacher)} has been activated.`))),
          switchMap( _ => this.loadTeachersPage(this.currentState))
        ).subscribe();
      },"Teacher activation", "bg-warning");

  }

  isActive(teacher: V3Teacher) {
    return teacher && teacher.status === LearningUnitTeacherStatus.ACTIVE;
  }

  canActivate(teacher: V3Teacher) {
    return teacher && teacher.status === LearningUnitTeacherStatus.BLOCKED;
  }

  changePasswordCallback = (_request: VerifiedPasswordChangeRequest) => of(null)

  changeEmailCallback = (_request: VerifiedEmailChangeRequest) => of(null)

  changePassword(teacher: V3Teacher) {
    this.changePasswordCallback = (request : VerifiedPasswordChangeRequest) =>
      this.teacherV3Rest.changeTeacherPasswordInstantly(this.schoolId, teacher.id, request)
    this.passwordModal.show();
  }

  changeEmail(teacher: V3Teacher) {
    this.changeEmailCallback = (request: VerifiedEmailChangeRequest) =>
      this.workflowsRest.requestTeacherEmailChange(this.schoolId, teacher.id, request)
    this.emailModal.show();
  }

  reloadTeacher(teacher: V3Teacher) {
    this.casa.syncTeacher(this.schoolId, teacher.id).subscribe(
      _ => this.toastNotificationService.display(
        new ToastNotification("Refreshing of teacher's eBooks & Exercises", `Teachers' ${this.getName(teacher)} eBooks & Exercises has been refreshed successfully. Teacher should be able to access all content.`, "bg-success text-white"))
    )
  }

  resetBookShares(teacher: V3Teacher) {
    this.confirmationService.doOnConfirm("Are you sure you to reset teacher eBooks download limit? This will remove the eBooks from all teacher's devices, so that they can download the eBooks to new devices.",
    () => {
      this.casa.clearTeacherDeviceShares(this.schoolId, teacher.id).subscribe(
        _ => this.toastNotificationService.display(
          new ToastNotification("Reset of teacher's eBooks download limit", `Teachers' ${this.getName(teacher)} eBooks download limit reset successfully.`)
        )
      )
    }, "Reset of teacher's eBooks download limit","bg-warning");
  }
}
